import React from 'react';
import { SchematicShell } from './SchematicPrimitives';

/**
 * Over-door hooks and over-door mirror elevations (pair with
 * dormOverDoorRenderer).
 *
 * Both hang from the same bracket that straddles the door top: a 2" front
 * plate visible from the room, with the rest of the object below it. What
 * makes each read correctly on an elevation is that bracket band — a plain
 * rectangle loses it and an over-door mirror becomes indistinguishable from a
 * wall mirror. Constants track the renderer's `buildBracket`.
 */
const IN = 0.0254;

const BRACKET_FRONT_H = 2 * IN; // buildBracket's frontH
const HOOK_COUNT = 5; // NUM_HOOKS
const HOOK_EDGE_INSET = 1.5 * IN; // half of the renderer's 3" hookSpan inset
const MIRROR_SIDE_INSET = 0.25 * IN; // half the renderer's 0.5" width trim
const MIRROR_TOP_GAP = 0.25 * IN;

function Bracket({ width, strokeColor, strokeWidth }) {
  return (
    <rect
      x={0}
      y={0}
      width={width}
      height={Math.min(BRACKET_FRONT_H, width)}
      fill={strokeColor}
      fillOpacity={0.14}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
    />
  );
}

export function DormOverDoorHooksSchematic({
  widthMeters: width,
  heightMeters: height,
  strokeColor,
  strokeWidth,
}) {
  const bracketH = Math.min(BRACKET_FRONT_H, height);
  const barY = bracketH;
  // Hooks are evenly spaced across a span inset from both edges.
  const span = Math.max(0, width - HOOK_EDGE_INSET * 2);
  const step = HOOK_COUNT > 1 ? span / (HOOK_COUNT - 1) : 0;
  const hookBottom = height;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      <Bracket
        width={width}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Hook bar */}
      <line
        x1={0}
        y1={barY}
        x2={width}
        y2={barY}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      {/* J-hooks dropping off the bar */}
      {Array.from({ length: HOOK_COUNT }, (_, i) => {
        const x = HOOK_EDGE_INSET + step * i;
        return (
          <line
            key={i}
            x1={x}
            y1={barY}
            x2={x}
            y2={hookBottom}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
    </SchematicShell>
  );
}

export function DormOverDoorMirrorSchematic({
  widthMeters: width,
  heightMeters: height,
  strokeColor,
  strokeWidth,
}) {
  const bracketH = Math.min(BRACKET_FRONT_H, height);
  const mirrorTop = bracketH + MIRROR_TOP_GAP;
  const inset = Math.min(MIRROR_SIDE_INSET, width / 4);
  const mirrorW = Math.max(0, width - inset * 2);
  const mirrorH = Math.max(0, height - mirrorTop);

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      <Bracket
        width={width}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {mirrorW > 0 && mirrorH > 0 && (
        <rect
          x={inset}
          y={mirrorTop}
          width={mirrorW}
          height={mirrorH}
          fill={strokeColor}
          fillOpacity={0.06}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </SchematicShell>
  );
}
